import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { api } from "@/lib/api";
import {
  getCurrentMunicipalityIdFromLegacyId,
  getLegacyMunicipalityName,
  normalizeMunicipalityName,
} from "@/lib/legacyMunicipalities";
import totemBackground from "@/assets/totem-background.png";
import type { NavButton } from "@/components/MunicipalityForm";

export type { NavButton };

export interface TotemConfig {
  city: string;
  lat: number;
  lon: number;
  municipality: string;
  municipalityId: string;
  apiKey?: string;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  primaryColor?: string;
  backgroundColor?: string;
  inactivityPromptEnabled: boolean;
  isLoading: boolean;
}

interface MunicipalityAssets {
  id?: string;
  name: string;
  lat?: number;
  lon?: number;
  heroes: Array<{
    name: string;
    imageUrl: string;
    clickUrl: string;
  }>;
  navButtons: NavButton[];
  bottomButtons: NavButton[];
  newsletterUrl: string;
  carpetaniaUrl: string;
  enjoyUrl: string;
  primaryColor?: string;
  backgroundColor?: string;
  inactivityPromptEnabled: boolean;
}

interface HeroDataDB {
  name: string;
  image_url: string;
  click_url: string;
}

// Fallback configuration map (used when DB is not available)
const FALLBACK_MUNICIPALITY_CONFIGS: Record<string, MunicipalityAssets> = {
  "Córdoba": {
    name: "Córdoba",
    lat: 37.8882,
    lon: -4.7794,
    heroes: [{
      name: "Córdoba",
      imageUrl: totemBackground,
      clickUrl: ""
    }],
    navButtons: [],
    bottomButtons: [],
    newsletterUrl: "",
    carpetaniaUrl: "",
    enjoyUrl: "",
    inactivityPromptEnabled: false,
  }
};

const URLConfigContext = createContext<TotemConfig | null>(null);

export const URLConfigProvider = ({ children }: { children: ReactNode }) => {
  const [municipalitiesFromDB, setMunicipalitiesFromDB] = useState<Record<string, MunicipalityAssets>>({});
  const [dbLoaded, setDbLoaded] = useState(false);

  // Almacenar los params de URL iniciales una sola vez al montar
  // Esto previene que navigate('/') cause pérdida de configuración
  const [initialParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedId = params.get('id')?.trim() || '';
    const legacyMunicipalityName = getLegacyMunicipalityName(requestedId);
    const requestedMunicipality =
      params.get('city') ||
      params.get('municipality') ||
      legacyMunicipalityName ||
      "Córdoba";
    const requestedLat = Number.parseFloat(params.get('lat') || '');
    const requestedLon = Number.parseFloat(params.get('lon') || '');

    return {
      requestedId,
      city: requestedMunicipality,
      lat: Number.isFinite(requestedLat) ? requestedLat : undefined,
      lon: Number.isFinite(requestedLon) ? requestedLon : undefined,
      apiKey: params.get('apiKey') || ''
    };
  });

  useEffect(() => {
    const loadMunicipalities = async () => {
      try {
        const data = await api.municipalities.list();

        if (data && data.length > 0) {
          const municipalitiesMap: Record<string, MunicipalityAssets> = {};
          data.forEach((municipality: any) => {
            const heroesData = municipality.heroes_data as HeroDataDB[];
            const heroes = Array.isArray(heroesData) && heroesData.length > 0
              ? heroesData.filter(hero => Boolean(hero.image_url)).map(hero => ({
                name: hero.name,
                imageUrl: hero.image_url,
                clickUrl: hero.click_url
              }))
              : [];

            municipalitiesMap[municipality.name] = {
              id: municipality.id,
              name: municipality.name,
              lat: Number.isFinite(Number(municipality.latitude))
                ? Number(municipality.latitude)
                : undefined,
              lon: Number.isFinite(Number(municipality.longitude))
                ? Number(municipality.longitude)
                : undefined,
              heroes,
              navButtons: (municipality.nav_buttons as NavButton[]) ?? [],
              bottomButtons: (municipality.bottom_buttons as NavButton[]) ?? [],
              newsletterUrl: municipality.newsletter_url,
              carpetaniaUrl: municipality.carpetania_url,
              enjoyUrl: municipality.enjoy_url,
              primaryColor: municipality.primary_color,
              backgroundColor: municipality.background_color,
              inactivityPromptEnabled: municipality.inactivity_prompt_enabled ?? false,
            };
          });
          setMunicipalitiesFromDB(municipalitiesMap);
        }
      } catch (error) {
        console.error('Error in loadMunicipalities:', error);
      } finally {
        setDbLoaded(true);
      }
    };

    loadMunicipalities();

    // Reload cuando la pestaña recupera el foco (sin realtime de Supabase)
    const handleFocus = () => loadMunicipalities();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('municipalityConfigUpdated', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('municipalityConfigUpdated', handleFocus);
    };
  }, []);

  const config = useMemo<TotemConfig>(() => {
    // Usar los valores almacenados en estado en lugar de window.location.search
    // para evitar pérdida de configuración cuando navigate('/') limpia los params
    const { requestedId, city, lat, lon, apiKey } = initialParams;

    // Use DB data if loaded, otherwise fallback to hardcoded config
    const MUNICIPALITY_CONFIGS = dbLoaded && Object.keys(municipalitiesFromDB).length > 0
      ? municipalitiesFromDB
      : FALLBACK_MUNICIPALITY_CONFIGS;

    // Resolver con varios fallbacks para nunca devolver undefined:
    // 1) municipio solicitado, 2) Córdoba, 3) primer disponible
    const availableMunicipalities = Object.values(MUNICIPALITY_CONFIGS);
    const currentIdFromLegacyId =
      getCurrentMunicipalityIdFromLegacyId(requestedId);
    const normalizedRequestedId = (
      currentIdFromLegacyId ?? requestedId
    ).toLowerCase();
    const legacyMunicipalityName = getLegacyMunicipalityName(requestedId);
    const requestedMunicipalityName = legacyMunicipalityName ?? city;
    const normalizedRequestedName = normalizeMunicipalityName(requestedMunicipalityName);
    const municipalityByCurrentId = normalizedRequestedId
      ? availableMunicipalities.find(
          (municipality) =>
            municipality.id?.toLowerCase() === normalizedRequestedId,
        )
      : undefined;
    const municipalityByName = availableMunicipalities.find(
      (municipality) =>
        normalizeMunicipalityName(municipality.name) === normalizedRequestedName,
    );
    const fallbackKey = Object.keys(MUNICIPALITY_CONFIGS)[0];
    const municipalityAssets =
      municipalityByCurrentId ??
      municipalityByName ??
      MUNICIPALITY_CONFIGS['Córdoba'] ??
      (fallbackKey ? MUNICIPALITY_CONFIGS[fallbackKey] : undefined);

    if (!municipalityAssets) {
      // BD vacía y sin fallback: devolver una configuración mínima en lugar de crashear
      return {
        city,
        lat: lat ?? 37.8882,
        lon: lon ?? -4.7794,
        municipality: city,
        municipalityId: '',
        apiKey,
        heroes: [],
        navButtons: [],
        bottomButtons: [],
        newsletterUrl: '',
        carpetaniaUrl: '',
        enjoyUrl: '',
        primaryColor: '#005041',
        backgroundColor: '#9EBEB3',
        inactivityPromptEnabled: false,
        isLoading: !dbLoaded,
      };
    }

    const selectedMunicipalityName = municipalityAssets.name;

    return {
      city: selectedMunicipalityName,
      lat: lat ?? municipalityAssets.lat ?? 37.8882,
      lon: lon ?? municipalityAssets.lon ?? -4.7794,
      municipality: selectedMunicipalityName,
      municipalityId: municipalityAssets.id || '',
      apiKey,
      heroes: municipalityAssets.heroes,
      navButtons: municipalityAssets.navButtons,
      bottomButtons: municipalityAssets.bottomButtons,
      newsletterUrl: municipalityAssets.newsletterUrl,
      carpetaniaUrl: municipalityAssets.carpetaniaUrl,
      enjoyUrl: municipalityAssets.enjoyUrl,
      primaryColor: municipalityAssets.primaryColor ?? '#005041',
      backgroundColor: municipalityAssets.backgroundColor ?? '#9EBEB3',
      inactivityPromptEnabled: municipalityAssets.inactivityPromptEnabled,
      isLoading: !dbLoaded
    };
  }, [municipalitiesFromDB, dbLoaded, initialParams]);

  return (
    <URLConfigContext.Provider value={config}>
      {children}
    </URLConfigContext.Provider>
  );
};

export const useURLConfigContext = (): TotemConfig => {
  const context = useContext(URLConfigContext);
  if (!context) {
    throw new Error('useURLConfigContext must be used within a URLConfigProvider');
  }
  return context;
};
